import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  Chip,
  Autocomplete,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Input,
} from '@mui/material';
import { CSOMember, CSOFormData } from '../types/cso';
import { useAddCSOMemberMutation, useUpdateCSOMemberMutation } from '../api/csoApi';

interface Props {
  open: boolean;
  setModalOpen: (open: boolean) => void;
  onSuccess?: () => void;
  csoMember?: CSOMember | null;
}

const AddEditCSO: React.FC<Props> = ({ open, setModalOpen, onSuccess, csoMember }) => {
  const [formData, setFormData] = useState<CSOFormData>({
    name: '',
    location: '',
    focal_person_name: '',
    focal_person_phone: '',
    thematic_areas: [],
    email: '',
    website: '',
    description: '',
    filled_xlsx: null,
    registration_certificate: null,
    rules_regulation_doc: null,
  });

  const [addCSOMember] = useAddCSOMemberMutation();
  const [updateCSOMember] = useUpdateCSOMemberMutation();
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Predefined thematic areas
  const thematicAreaOptions = [
    'Youth Development',
    'Education',
    'Women\'s Rights',
    'Health',
    'Environment',
    'Human Rights',
    'Community Development',
    'Advocacy',
    'Capacity Building',
    'Research',
    'Disaster Relief',
    'Economic Development',
    'Social Services',
    'Cultural Preservation',
    'Technology',
  ];

  useEffect(() => {
    if (csoMember) {
      setFormData({
        name: csoMember.name || '',
        location: csoMember.location || '',
        focal_person_name: csoMember.focal_person_name || '',
        focal_person_phone: csoMember.focal_person_phone || '',
        thematic_areas: csoMember.thematic_areas || [],
        email: csoMember.email || '',
        website: csoMember.website || '',
        description: csoMember.description || '',
        filled_xlsx: null,
        registration_certificate: null,
        rules_regulation_doc: null,
      });
    } else {
      setFormData({
        name: '',
        location: '',
        focal_person_name: '',
        focal_person_phone: '',
        thematic_areas: [],
        email: '',
        website: '',
        description: '',
        filled_xlsx: null,
        registration_certificate: null,
        rules_regulation_doc: null,
      });
    }
    setSubmissionError(null);
  }, [csoMember, open]);

  const handleInputChange = (field: keyof CSOFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange = (field: 'filled_xlsx' | 'registration_certificate' | 'rules_regulation_doc') => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] || null;
      handleInputChange(field, file);
    };
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmissionError(null);

    try {
      // Create FormData for file uploads
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('location', formData.location);
      formDataToSend.append('focal_person_name', formData.focal_person_name);
      formDataToSend.append('focal_person_phone', formData.focal_person_phone);
      formDataToSend.append('thematic_areas', JSON.stringify(formData.thematic_areas));
      formDataToSend.append('email', formData.email || '');
      if (formData.website) {
        formDataToSend.append('website', formData.website);
      }
      if (formData.description) {
        formDataToSend.append('description', formData.description);
      }

      // Append files if they exist
      if (formData.filled_xlsx) {
        formDataToSend.append('filled_xlsx', formData.filled_xlsx);
      }
      if (formData.registration_certificate) {
        formDataToSend.append('registration_certificate', formData.registration_certificate);
      }
      if (formData.rules_regulation_doc) {
        formDataToSend.append('rules_regulation_doc', formData.rules_regulation_doc);
      }

      if (csoMember) {
        await updateCSOMember({
          id: csoMember.id.toString(),
          body: formDataToSend,
        }).unwrap();
      } else {
        await addCSOMember({
          body: formDataToSend,
        }).unwrap();
      }

      setModalOpen(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      setSubmissionError(
        error?.data?.message || 
        `Failed to ${csoMember ? 'update' : 'create'} CSO member`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setModalOpen(false);
    setSubmissionError(null);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{csoMember ? 'Edit CSO Member' : 'Add New CSO Member'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {submissionError && <Alert severity="error">{submissionError}</Alert>}

          <TextField
            label="Organization Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            fullWidth
            required
          />

          <TextField
            label="Location"
            value={formData.location}
            onChange={(e) => handleInputChange('location', e.target.value)}
            fullWidth
            required
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Focal Person Name"
              value={formData.focal_person_name}
              onChange={(e) => handleInputChange('focal_person_name', e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Focal Person Phone"
              value={formData.focal_person_phone}
              onChange={(e) => handleInputChange('focal_person_phone', e.target.value)}
              fullWidth
              required
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              fullWidth
              type="email"
              required
            />
            <TextField
              label="Website"
              value={formData.website}
              onChange={(e) => handleInputChange('website', e.target.value)}
              fullWidth
              placeholder="https://example.org"
            />
          </Box>

          <Autocomplete
            multiple
            options={thematicAreaOptions}
            value={formData.thematic_areas}
            onChange={(event, newValue) => {
              handleInputChange('thematic_areas', newValue);
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={option}
                  {...getTagProps({ index })}
                  key={option}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Thematic Areas"
                placeholder="Select thematic areas"
                helperText="Select one or more thematic areas"
              />
            )}
          />

          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            fullWidth
            multiline
            rows={4}
            placeholder="Enter organization description..."
          />

          <Typography variant="h6" sx={{ mt: 2, mb: 1, fontSize: '1rem', fontWeight: 600 }}>
            Documents
          </Typography>

          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Filled XLSX {!csoMember && '*'}
            </Typography>
            <Input
              type="file"
              inputProps={{ accept: '.xlsx,.xls,.csv' }}
              onChange={handleFileChange('filled_xlsx')}
              fullWidth
            />
            {formData.filled_xlsx && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Selected: {formData.filled_xlsx.name}
              </Typography>
            )}
            {csoMember && csoMember.filled_xlsx_url && !formData.filled_xlsx && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Current file: <a href={csoMember.filled_xlsx_url} target="_blank" rel="noopener noreferrer">View current file</a>
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Registration Certificate {!csoMember && '*'}
            </Typography>
            <Input
              type="file"
              inputProps={{ accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png' }}
              onChange={handleFileChange('registration_certificate')}
              fullWidth
            />
            {formData.registration_certificate && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Selected: {formData.registration_certificate.name}
              </Typography>
            )}
            {csoMember && csoMember.registration_certificate_url && !formData.registration_certificate && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Current file: <a href={csoMember.registration_certificate_url} target="_blank" rel="noopener noreferrer">View current file</a>
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Rules & Regulation Document {!csoMember && '*'}
            </Typography>
            <Input
              type="file"
              inputProps={{ accept: '.pdf,.doc,.docx' }}
              onChange={handleFileChange('rules_regulation_doc')}
              fullWidth
            />
            {formData.rules_regulation_doc && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Selected: {formData.rules_regulation_doc.name}
              </Typography>
            )}
            {csoMember && csoMember.rules_regulation_doc_url && !formData.rules_regulation_doc && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Current file: <a href={csoMember.rules_regulation_doc_url} target="_blank" rel="noopener noreferrer">View current file</a>
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : csoMember ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditCSO;
